import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button, Skeleton, Tag } from "antd";
import {
  EditOutlined,
  EyeOutlined,
  HistoryOutlined,
  QrcodeOutlined,
  PrinterOutlined,
  SettingOutlined,
  WarningOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import type { GeoJsonFeature, GravePositionProperties } from "./gisApi";
import { getGrave, type GraveData } from "../../graves/graveApi";
import { formatDateVn } from "../../../lib/dateFormat";
import { GravePublicStatusControl } from "./GravePublicStatusControl";
import {
  gisIdentityColor,
  gisIdentityStatusLabel,
  gisPositionStateLabel,
  gisPositionStateTone,
  type GisTone,
} from "./gisVocabulary";
import "./gisAdmin.css";

/**
 * `GisTone` (nghiệp vụ) → màu preset của AntD Tag. Giữ CỤC BỘ ở đây: `gisVocabulary.ts` hiện
 * không import gì và phải giữ thuần — trộn AntD vào đó là kéo tầng trình bày xuống tận từ vựng.
 */
const POSITION_STATE_TAG_COLOR: Readonly<Record<GisTone, string>> = {
  success: "success",
  danger: "error",
  warning: "warning",
  info: "processing",
  neutral: "default",
};

export interface GisDetailRailProps {
  readonly selectedFeature: GeoJsonFeature | null;
  readonly cemeteryId?: string;
  readonly onOpenEditModal: () => void;
  readonly onOpenHistory?: () => void;
  readonly onOpenQr?: () => void;
  readonly onPrint?: () => void;
  readonly onOpenPublicPreview?: () => void;
  /**
   * Bề mặt CHỈ ĐỌC — tab "Bản đồ" nhúng trong hồ sơ nghĩa trang. Ẩn hành động GHI, giữ hành
   * động ĐỌC. Mặc định `false` để route riêng `/cemeteries/:cemeteryId/gis` đi nguyên đường
   * biên tập; đây là đường may duy nhất giữa hai bề mặt.
   */
  readonly readOnly?: boolean;
}

export function GisDetailRail({
  selectedFeature,
  cemeteryId,
  onOpenEditModal,
  onOpenHistory,
  onOpenQr,
  onPrint,
  onOpenPublicPreview,
  readOnly = false,
}: GisDetailRailProps) {
  const [showTechBlock, setShowTechBlock] = useState(false);
  const [graveDetail, setGraveDetail] = useState<GraveData | null>(null);
  const [loadingGrave, setLoadingGrave] = useState(false);

  const rawProps = (selectedFeature?.properties || {}) as GravePositionProperties;
  const graveId = rawProps.graveId;

  useEffect(() => {
    if (!graveId) {
      setGraveDetail(null);
      setLoadingGrave(false);
      return;
    }
    let cancelled = false;
    setLoadingGrave(true);
    getGrave(graveId)
      .then((data) => {
        if (!cancelled) {
          setGraveDetail(data);
          setLoadingGrave(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoadingGrave(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [graveId]);

  if (!selectedFeature) {
    return (
      <div
        data-testid="gis-detail-rail"
        className="gis-detail-rail-wrap"
        style={{ alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", color: "var(--ncc-color-text-secondary)" }}
      >
        <EnvironmentOutlined style={{ fontSize: 36, color: "var(--ncc-color-border-default)", marginBottom: 12 }} />
        <p style={{ fontWeight: 600, color: "var(--ncc-color-text-primary)", margin: 0 }}>
          Chưa chọn vị trí mộ nào
        </p>
        <p style={{ fontSize: 11, color: "var(--ncc-color-text-secondary)", marginTop: 4 }}>
          Nhấp chuột vào một ô mộ trên bản đồ hoặc trong bảng để xem chi tiết.
        </p>
      </div>
    );
  }

  const props = (selectedFeature.properties || {}) as GravePositionProperties;
  const coords =
    selectedFeature.geometry?.type === "Point" &&
    Array.isArray(selectedFeature.geometry.coordinates)
      ? (selectedFeature.geometry.coordinates as [number, number])
      : null;

  // Trích xuất thông tin liệt sĩ từ link đang hiệu lực của hồ sơ mộ.
  // MỘT nguồn duy nhất: `martyrLinks[]` của `GET /graves/{id}`. Bản trước còn nhánh dự phòng
  // `props.<trường>` (thuộc tính feature bản đồ) nhưng từ vựng của feature KHÔNG có khoá nào về
  // liệt sĩ, nên nhánh đó luôn `undefined` — giữ lại chỉ che mất việc nguồn thật có chở dữ liệu
  // hay không. Riêng `props.martyrName` vẫn còn trong kiểu vì `GisTableView` đang đọc nó.
  const activeMartyrLink = graveDetail?.martyrLinks?.find((l) => !l.effectiveTo) ?? graveDetail?.martyrLinks?.[0];
  const effectiveMartyrName = activeMartyrLink?.martyrDisplayName || null;
  const effectiveRank = activeMartyrLink?.rankName || null;
  const effectivePosition = activeMartyrLink?.positionName || null;
  const effectiveUnit = activeMartyrLink?.unitName || null;
  const effectiveNativePlace = activeMartyrLink?.nativePlace || null;
  const effectiveBirthYear = activeMartyrLink?.birthYear || null;
  const effectiveSacrificeDate = activeMartyrLink?.sacrificeDate || null;
  const effectiveMartyrCode = activeMartyrLink?.martyrCode || null;

  const isUnknown = props.identityStatus === "UNKNOWN";
  const hasMartyrInfo = Boolean(effectiveMartyrName || effectiveRank || effectivePosition || effectiveUnit || effectiveNativePlace);

  return (
    <div data-testid="gis-detail-rail" className="gis-detail-rail-wrap">
      {/* Rail Header — nguồn DUY NHẤT của mã vị trí và trạng thái an táng. Hai giá trị này
          từng được in lại thành hai dòng riêng ngay bên dưới, cách header vài chục pixel. */}
      <div className="gis-rail-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ncc-color-text-secondary)", textTransform: "uppercase" }}>
            CHI TIẾT VỊ TRÍ MỘ
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ncc-color-text-primary)", marginTop: 2, fontFamily: "monospace" }}>
            {props.positionCode || "—"}
          </div>
        </div>
        {/* Nhãn + màu đều tra từ `gisVocabulary` (DB thắng): `ck_position_state` có BA giá trị
            AVAILABLE | OCCUPIED | CLOSED. Bản cũ là tam phân cứng `isOccupied ? … : "Ô trống"`
            nên CLOSED rơi vào nhánh else ⇒ in "Ô trống" màu XANH, ngược giá trị DB. */}
        <Tag
          color={POSITION_STATE_TAG_COLOR[gisPositionStateTone(props.positionState)]}
          style={{ fontWeight: 700 }}
        >
          {gisPositionStateLabel(props.positionState)}
        </Tag>
      </div>

      {/* Rail Body */}
      <div className="gis-rail-body">
        {/* Banner toạ độ TEMPORARY */}
        {props.verificationStatus === "TEMPORARY" && (
          <div style={{ background: "var(--ncc-color-status-warning-bg)", border: "1px solid var(--ncc-color-border-default)", borderRadius: "var(--ncc-radius-md)", padding: 10 }}>
            <div style={{ fontWeight: 700, color: "var(--ncc-color-status-warning)", fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
              <WarningOutlined />
              Trạng thái toạ độ: Tạm thời
            </div>
            <div style={{ fontSize: 11, color: "var(--ncc-color-status-warning)", marginTop: 4 }}>
              Toạ độ nội suy từ sơ đồ nguồn, chưa được xác nhận thực địa.
            </div>
          </div>
        )}

        {/* Metadata List */}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--ncc-color-border-default)", paddingBottom: 6 }}>
            <span style={{ color: "var(--ncc-color-text-secondary)" }}>Khu vực / Lô / Hàng:</span>
            <span style={{ fontWeight: 600, color: "var(--ncc-color-text-primary)" }}>
              {props.sectionCode || props.sectionName || "—"}
            </span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--ncc-color-border-default)", paddingBottom: 6 }}>
            <span style={{ color: "var(--ncc-color-text-secondary)" }}>Tình trạng danh tính:</span>
            <span
              style={{
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: 6,
                color: gisIdentityColor(props.identityStatus).stroke,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: gisIdentityColor(props.identityStatus).fill,
                  display: "inline-block",
                }}
              />
              {gisIdentityStatusLabel(props.identityStatus)}
            </span>
          </div>

          {/* Thông tin Liệt sĩ / Người an táng nổi bật */}
          {loadingGrave && !hasMartyrInfo ? (
            <div style={{ padding: "10px 12px", border: "1px solid var(--ncc-color-border-default)", borderRadius: "var(--ncc-radius-md, 6px)" }}>
              <Skeleton active paragraph={{ rows: 2 }} />
            </div>
          ) : isUnknown && !hasMartyrInfo ? (
            <div
              style={{
                background: "var(--ncc-color-bg-subtle)",
                border: "1px solid var(--ncc-color-border-default)",
                borderRadius: "var(--ncc-radius-md, 6px)",
                padding: "10px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ncc-color-text-secondary)", textTransform: "uppercase" }}>
                Thông tin người an táng / Liệt sĩ
              </div>
              <div style={{ fontSize: 13, fontStyle: "italic", color: "var(--ncc-color-text-secondary)" }}>
                Mộ chưa xác định được danh tính liệt sĩ (vô danh / khuyết thông tin).
              </div>
            </div>
          ) : (
            <div
              style={{
                background: "var(--ncc-color-bg-subtle)",
                border: "1px solid var(--ncc-color-border-default)",
                borderRadius: "var(--ncc-radius-md, 6px)",
                padding: "10px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ncc-color-text-secondary)", textTransform: "uppercase" }}>
                Thông tin người an táng / Liệt sĩ
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--ncc-color-text-primary)" }}>
                {/* Tab mới như link hồ sơ mộ bên dưới — không đánh mất vị trí đang xem trên bản đồ. */}
                {effectiveMartyrName && activeMartyrLink?.martyrId ? (
                  <Link
                    to={`/martyrs/${encodeURIComponent(activeMartyrLink.martyrId)}`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--ncc-color-action-primary)" }}
                  >
                    {effectiveMartyrName}
                  </Link>
                ) : (
                  effectiveMartyrName || (props.identityStatus === "KNOWN" ? "Đang tải thông tin liệt sĩ..." : "Chưa có liên kết hồ sơ liệt sĩ")
                )}
              </div>
              {(effectiveRank || effectivePosition) && (
                <div style={{ fontSize: 12, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "var(--ncc-color-text-secondary)" }}>Cấp bậc / Chức vụ:</span>
                  <span style={{ fontWeight: 600, color: "var(--ncc-color-text-primary)" }}>
                    {[effectiveRank, effectivePosition].filter(Boolean).join(" - ")}
                  </span>
                </div>
              )}
              {effectiveUnit && (
                <div style={{ fontSize: 12, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "var(--ncc-color-text-secondary)" }}>Đơn vị:</span>
                  <span style={{ fontWeight: 600, color: "var(--ncc-color-text-primary)" }}>
                    {effectiveUnit}
                  </span>
                </div>
              )}
              {effectiveNativePlace && (
                <div style={{ fontSize: 12, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "var(--ncc-color-text-secondary)" }}>Quê quán:</span>
                  <span style={{ color: "var(--ncc-color-text-primary)" }}>
                    {effectiveNativePlace}
                  </span>
                </div>
              )}
              {(effectiveBirthYear || effectiveSacrificeDate) && (
                <div style={{ fontSize: 12, display: "flex", justifyContent: "space-between" }}>
                  <span style={{ color: "var(--ncc-color-text-secondary)" }}>Sinh / Hy sinh:</span>
                  <span style={{ color: "var(--ncc-color-text-primary)" }}>
                    {effectiveBirthYear ? `Năm sinh: ${effectiveBirthYear}` : ""}
                    {effectiveBirthYear && effectiveSacrificeDate ? " · " : ""}
                    {/* Quy ước ngày toàn app là dd-mm-yyyy (`lib/dateFormat.ts`) — in ISO trần ở
                        đây là lệch với mọi màn khác. Dòng này trước 18/09 chưa từng hiện được
                        (7 trường không có nguồn) nên lệch chưa ai thấy. */}
                    {effectiveSacrificeDate
                      ? `Hy sinh: ${formatDateVn(effectiveSacrificeDate)}`
                      : ""}
                  </span>
                </div>
              )}
              {effectiveMartyrCode && (
                <div style={{ fontSize: 11, display: "flex", justifyContent: "space-between", color: "var(--ncc-color-text-secondary)" }}>
                  <span>Mã hồ sơ liệt sĩ:</span>
                  <span style={{ fontFamily: "monospace" }}>{effectiveMartyrCode}</span>
                </div>
              )}
            </div>
          )}

          {props.graveId && (
            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--ncc-color-border-default)", paddingBottom: 6 }}>
              <span style={{ color: "var(--ncc-color-text-secondary)" }}>Hồ sơ mộ gắn kết:</span>
              <Link
                to={`/graves/${encodeURIComponent(props.graveId)}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: "var(--ncc-color-action-primary)", fontWeight: 600 }}
              >
                {props.graveCode || "Xem hồ sơ"} ↗
              </Link>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--ncc-color-border-default)", paddingBottom: 6 }}>
            <span style={{ color: "var(--ncc-color-text-secondary)" }}>Tình trạng vật lý vỏ mộ:</span>
            {/* Cột này là text TỰ DO dưới DB ("Nguyên trạng", "Cần tu bổ nắp"…) — in nguyên văn. */}
            <span>{props.physicalCondition || "—"}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--ncc-color-border-default)", paddingBottom: 6 }}>
            <span style={{ color: "var(--ncc-color-text-secondary)" }}>Nguồn gốc toạ độ:</span>
            <span>{props.sourceType || "Chưa xác định"}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid var(--ncc-color-border-default)", paddingBottom: 6 }}>
            <span style={{ color: "var(--ncc-color-text-secondary)" }}>Sai số độ chính xác:</span>
            <strong style={{ color: "var(--ncc-color-text-primary)" }}>
              {props.precisionMeters != null ? `± ${props.precisionMeters} mét` : "—"}
            </strong>
          </div>
        </div>

        {/* Khối Kỹ Thuật Collapsible — Ngoại lệ §5.2 đã duyệt (spec S12.5 §5.2): `sourceSrid` và
            thứ tự trục chỉ được sống TRONG khối này, không ra vùng chính. Trích dẫn điều khoản
            nằm ở đây chứ không in lên nhãn — nhãn là chữ cho người dùng, không phải chỗ dẫn spec. */}
        <div style={{ border: "1px solid var(--ncc-color-border-default)", borderRadius: "var(--ncc-radius-md)", overflow: "hidden" }}>
          <button
            type="button"
            onClick={() => setShowTechBlock((s) => !s)}
            style={{
              width: "100%",
              background: "var(--ncc-color-bg-subtle)",
              border: "none",
              padding: "8px 12px",
              textAlign: "left",
              fontWeight: 600,
              fontSize: 12,
              color: "var(--ncc-color-text-primary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <SettingOutlined style={{ color: "var(--ncc-color-text-secondary)" }} />
              Thông số toạ độ kỹ thuật
            </span>
            <span style={{ color: "var(--ncc-color-text-secondary)" }}>{showTechBlock ? "▲" : "▼"}</span>
          </button>
          {showTechBlock && (
            <div style={{ padding: 10, background: "var(--ncc-color-bg-surface)", fontSize: 11, borderTop: "1px solid var(--ncc-color-border-default)", display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Hệ toạ độ (sourceSrid):</span>
                <strong style={{ fontFamily: "monospace" }}>EPSG:4326 (WGS 84)</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Thứ tự trục toạ độ:</span>
                <strong style={{ fontFamily: "monospace" }}>lon, lat (x, y)</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Kinh độ (Longitude):</span>
                <strong style={{ fontFamily: "monospace", color: "var(--ncc-color-action-primary)" }}>
                  {coords ? coords[0].toFixed(6) : "—"}
                </strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span>Vĩ độ (Latitude):</span>
                <strong style={{ fontFamily: "monospace", color: "var(--ncc-color-action-primary)" }}>
                  {coords ? coords[1].toFixed(6) : "—"}
                </strong>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Rail Action Footer */}
      <div className="gis-rail-actions">
        {readOnly ? null : (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={onOpenEditModal}
            size="middle"
            block
            style={{ fontWeight: 700 }}
            aria-label="Cập nhật vị trí toạ độ"
          >
            Cập nhật vị trí toạ độ
          </Button>
        )}

        <div className="gis-rail-actions-row">
          {/* `Lịch sử`/`Mã QR` mới chỉ gọi `alert()` — chưa từng hoạt động. Ở bề mặt chỉ đọc
              chúng là nút chết nên không render; route riêng giữ nguyên tới khi có CR làm thật. */}
          {readOnly ? null : (
            <>
              <Button icon={<HistoryOutlined />} onClick={onOpenHistory} size="small">
                Lịch sử
              </Button>
              <Button icon={<QrcodeOutlined />} onClick={onOpenQr} size="small">
                Mã QR
              </Button>
            </>
          )}
          <Button icon={<PrinterOutlined />} onClick={onPrint} size="small">
            In sơ đồ
          </Button>
        </div>

        {onOpenPublicPreview && (
          <Button
            icon={<EyeOutlined />}
            onClick={onOpenPublicPreview}
            size="small"
            block
            style={{ color: "var(--ncc-color-status-success)", borderColor: "var(--ncc-color-status-success)" }}
          >
            Xem trước hiển thị công khai
          </Button>
        )}

        {Boolean(cemeteryId && props.graveId) && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: "1px dashed var(--ncc-color-border-default)" }}>
            <GravePublicStatusControl
              cemeteryId={cemeteryId!}
              graveId={props.graveId!}
            />
          </div>
        )}
      </div>
    </div>
  );
}
